import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import * as Sentry from "@sentry/nextjs";
export type ErrorFallbackProps = {
  error: unknown;
  componentStack?: string;
  resetError: () => void;
};
export type BaseErrorBoundaryProps = Omit<Sentry.ErrorBoundaryProps, "fallback"> & {
  fallback?: (props: ErrorFallbackProps) => React.ReactElement;
};

/**
 * Base ErrorBoundary component that wraps Sentry's ErrorBoundary.
 *
 * This is a pure functional component with no UI dependencies.
 * The default fallback is minimal (just text) - for styled UI fallbacks,
 * use the `ErrorBoundary` wrapper from `@repo/ui/components/ui/error-boundary`.
 *
 * @example
 * ```tsx
 * import { BaseErrorBoundary } from "@repo/common/components";
 *
 * // Minimal fallback (default)
 * <BaseErrorBoundary>
 *   {children}
 * </BaseErrorBoundary>
 *
 * // Custom fallback
 * <BaseErrorBoundary fallback={(props) => <CustomFallback {...props} />}>
 *   {children}
 * </BaseErrorBoundary>
 * ```
 */
export function BaseErrorBoundary(t0) {
  const $ = _c(10);
  let children;
  let fallback;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      fallback,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = fallback;
    $[3] = props;
  } else {
    children = $[1];
    fallback = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== fallback) {
    t1 = fallback ? t2 => {
      const {
        error,
        componentStack,
        resetError
      } = t2;
      return fallback({
        error,
        componentStack,
        resetError
      });
    } : _temp;
    $[4] = fallback;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <Sentry.ErrorBoundary {...props} fallback={t1}>{children}</Sentry.ErrorBoundary>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}

/**
 * Higher-order component that wraps a component with a BaseErrorBoundary
 */
function _temp() {
  return <span>Error occurred</span>;
}
export function withBaseErrorBoundary<P extends object>(Component: React.ComponentType<P>, fallback?: BaseErrorBoundaryProps["fallback"]): React.FC<P> {
  const displayName = Component.displayName || Component.name || "Component";
  const WrappedComponent: React.FC<P> = props => {
    const $ = _c(2);
    let t0;
    if ($[0] !== props) {
      t0 = <BaseErrorBoundary fallback={fallback}><Component {...props} /></BaseErrorBoundary>;
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  };
  WrappedComponent.displayName = `withBaseErrorBoundary(${displayName})`;
  return WrappedComponent;
}